/**
 * 線区マスタ (`routes-master.json`) のフェッチ。
 *
 * - WebApp 内に同梱した routes-master.json を `/data/routes-master.json` 経由で読み込む。
 * - SettingBucket からダウンロードする運用も可能だが、フロントエンドからは
 *   CloudFront 配下の同居アセットとして配信した方がシンプル。
 */

export interface RouteDirection {
  direction_name: string;
  /**
   * 方向 ID。`routes-master.json` の各方面に明示された `direction_id` フィールド
   * (例: `"0"` / `"1"`) に対応する。GTFS-RT TripUpdate の `trip.directionId` と
   * 一致させる必要があり、Transformer 側の解釈が他路線と逆の路線
   * (例: 中総緩は 0 が三鷹、1 が千葉) は本フィールドで明示する。
   *
   * 後方互換のため optional。未指定の古いデータは配列順 (index 0 = "0",
   * index 1 = "1") にフォールバックする。
   */
  direction_id?: string;
}

export interface RouteMaster {
  route_id: string;
  route_name: string;
  directions: RouteDirection[];
}

export interface RoutesMasterFile {
  routes: RouteMaster[];
}

/** routes-master.json を fetch して返す。 */
export async function fetchRoutesMaster(): Promise<RoutesMasterFile> {
  const res = await fetch('/data/routes-master.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to fetch routes-master.json: HTTP ${res.status}`);
  }
  return (await res.json()) as RoutesMasterFile;
}

/** 線区 + 方面 を `${route_id}#${direction_name}` 形式の routeDirection に変換 */
export function toRouteDirection(routeId: string, directionName: string): string {
  return `${routeId}#${directionName}`;
}

/** routeDirection 文字列を線区/方面に分解する。`#` が含まれない場合は両方空。 */
export function parseRouteDirection(value: string): { routeId: string; directionName: string } {
  const idx = value.indexOf('#');
  if (idx < 0) return { routeId: value, directionName: '' };
  return { routeId: value.slice(0, idx), directionName: value.slice(idx + 1) };
}
